import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Radio,
  Users,
  BarChart3,
  FileText,
  Clock,
  Settings,
  LogOut,
  ChevronLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import logoWhite from "@/assets/logo-white.png";
import { motion } from "framer-motion";
import { useSidebar } from "@/contexts/SidebarContext";
import { useAuth } from "@/contexts/AuthContext";
import { useScheduleFeedback } from "@/hooks/useScheduleFeedback";
import { useProductionPagePrefetch } from "@/hooks/useProductionPagePrefetch";
import { productionPageFromPath } from "@/lib/prefetch/productionPagePrefetch";
import { Badge } from "@/components/ui/badge";

interface ProductionSidebarProps {
  productionId: string;
}

const ProductionSidebar = ({ productionId }: ProductionSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { expanded, setExpanded } = useSidebar();
  const { isCoProducerOnly } = useAuth();
  // Unread client feedback drives the badge on the "Schedule" nav item.
  const { unreadCount } = useScheduleFeedback(productionId);
  // Warm the other pages of this production: on hover of a nav item, and on
  // browser idle for the producer who clicks straight through.
  const { prefetchPage } = useProductionPagePrefetch(productionId);
  const warm = (path: string) => prefetchPage(productionPageFromPath(path, productionId));

  const isProductionDashboard = location.pathname === `/productions/${productionId}`;
  const isSubPage = location.pathname.startsWith(`/productions/${productionId}/`);
  // A co-producer has no layer above this production: hide "Settings" and the
  // back-to-dashboard control (they can still go back from a sub-page).
  const showBack = isSubPage || (isProductionDashboard && !isCoProducerOnly);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/producer-login");
  };

  const handleBack = () => {
    if (isSubPage) {
      // From subpage → production dashboard
      navigate(`/productions/${productionId}`);
    } else if (isProductionDashboard) {
      // From production dashboard → main dashboard
      navigate("/dashboard");
    }
  };

  const navItems = [
    { path: `/productions/${productionId}`, icon: LayoutDashboard, label: "Overview" },
    { path: `/productions/${productionId}/live-tracking`, icon: Radio, label: "Live tracking" },
    { path: `/productions/${productionId}/crew`, icon: Users, label: "Manage crew" },
    { path: `/productions/${productionId}/analytics`, icon: BarChart3, label: "Analytics" },
    { path: `/productions/${productionId}/briefing`, icon: FileText, label: "Briefing" },
    { path: `/productions/${productionId}/schedule`, icon: Clock, label: "Schedule" },
  ];

  return (
    <motion.aside
      className="fixed z-50 flex h-screen flex-col overflow-hidden border-r border-border bg-background py-6"
      animate={{ width: expanded ? 220 : 72 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{ pointerEvents: "auto" }}
    >
      {/* Logo / Back Button */}
      <div className="mb-8 px-3">
        {showBack ? (
          <button
            onClick={handleBack}
            className="flex h-12 w-full items-center rounded-lg transition-colors hover:bg-muted/10"
            aria-label="Back"
          >
            <ChevronLeft className="h-6 w-6 flex-shrink-0 text-muted-foreground" />
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="ml-2 whitespace-nowrap text-muted-foreground"
              >
                Back
              </motion.span>
            )}
          </button>
        ) : isCoProducerOnly ? (
          // Co-producer: logo only (no link to the forbidden dashboard),
          // aligned with the nav icon column below.
          <div className="flex h-12 items-center" aria-label="PROduce">
            <div className="flex w-12 flex-shrink-0 items-center justify-center">
              <img src="/produce-favicon.png" alt="PROduce" className="h-8 w-8 object-contain" />
            </div>
          </div>
        ) : (
          <Link to="/dashboard" className="flex h-12 items-center">
            <img src={logoWhite} alt="PROduce" className="h-8 w-auto" />
          </Link>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex flex-1 flex-col gap-2 px-3">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const badgeCount = item.path.endsWith("/schedule") ? unreadCount : 0;

          return (
            <Link
              key={item.path}
              to={item.path}
              // Start the page's reads while the pointer is still on its way to
              // the click — see useProductionPagePrefetch.
              onMouseEnter={() => warm(item.path)}
              onFocus={() => warm(item.path)}
              onTouchStart={() => warm(item.path)}
              className={cn(
                "flex h-12 items-center rounded-lg transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/10 hover:text-foreground",
              )}
              aria-label={
                badgeCount > 0 ? `${item.label} (${badgeCount} new feedback)` : item.label
              }
            >
              <div className="relative flex w-12 flex-shrink-0 items-center justify-center">
                <Icon className="h-5 w-5" />
                {/* Collapsed: a dot on the icon signals new feedback. */}
                {badgeCount > 0 && !expanded && (
                  <span className="absolute right-2 top-1 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background" />
                )}
              </div>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="flex flex-1 items-center justify-between whitespace-nowrap pr-3"
                >
                  {item.label}
                  {badgeCount > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {badgeCount}
                    </Badge>
                  )}
                </motion.span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className="flex flex-col gap-2 px-3">
        {!isCoProducerOnly && (
          <Link
            to="/settings"
            className="flex h-12 items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/10 hover:text-foreground"
            aria-label="Settings"
          >
            <div className="flex w-12 flex-shrink-0 items-center justify-center">
              <Settings className="h-5 w-5" />
            </div>
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="whitespace-nowrap"
              >
                Settings
              </motion.span>
            )}
          </Link>
        )}
        <button
          onClick={handleLogout}
          className="flex h-12 items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/10 hover:text-foreground"
          aria-label="Logout"
        >
          <div className="flex w-12 flex-shrink-0 items-center justify-center">
            <LogOut className="h-5 w-5" />
          </div>
          {expanded && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="whitespace-nowrap"
            >
              Logout
            </motion.span>
          )}
        </button>
      </div>
    </motion.aside>
  );
};

export default ProductionSidebar;
